/**
 * NotificationList Component
 *
 * Grouped list of notifications organized by priority.
 * Also shows system notifications (toast messages) when filtered.
 */

import { useMemo } from 'react';
import { AlertTriangle, Info, CheckCircle, XCircle } from 'lucide-react';
import { formatDistanceToNowStrict } from 'date-fns';
import { useNotificationContext } from '@/context/NotificationContext';
import { CenterToast, NotificationItem } from './NotificationItem';
import type { Notification, NotificationPriority, SystemNotification } from '@/types/notifications';

const PRIORITY_GROUPS: { priority: NotificationPriority; label: string }[] = [
  { priority: 'urgent', label: 'Urgent' },
  { priority: 'action_required', label: 'Action Required' },
  { priority: 'info', label: 'Recent' },
];

// System notification item component
const SYSTEM_ICONS = {
  success: CheckCircle,
  error: XCircle,
  warning: AlertTriangle,
  info: Info,
  default: Info,
} as const;

function SystemNotificationItem({ notification }: { notification: SystemNotification }) {
  const { dismissSystemNotification } = useNotificationContext();
  const tone = notification.type === 'success' || notification.type === 'error' || notification.type === 'warning'
    ? notification.type
    : 'default';
  const meta = [notification.category, notification.businessName, formatDistanceToNowStrict(notification.timestamp, { addSuffix: true })]
    .filter(Boolean)
    .join(' · ');

  return (
    <CenterToast
      title={notification.title}
      description={notification.description}
      icon={tone === 'default' ? undefined : SYSTEM_ICONS[notification.type]}
      tone={tone}
      meta={meta}
      onDismiss={() => dismissSystemNotification(notification.id)}
    />
  );
}

export function NotificationList() {
  const { notifications, isLoading, filter, systemNotifications } = useNotificationContext();

  // Group notifications by priority (only when showing 'all')
  const groupedNotifications = useMemo(() => {
    if (filter !== 'all' && filter !== 'system') {
      // When filtered by priority, show as flat list
      return new Map<NotificationPriority, Notification[]>([
        [filter as NotificationPriority, notifications]
      ]);
    }

    // Group by priority
    const groups = new Map<NotificationPriority, Notification[]>();

    notifications.forEach(notification => {
      const existing = groups.get(notification.priority) || [];
      groups.set(notification.priority, [...existing, notification]);
    });

    return groups;
  }, [notifications, filter]);

  if (isLoading && filter !== 'system') {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-6 w-6 border-2 border-primary border-t-transparent" />
      </div>
    );
  }

  // Show system notifications when filter is 'system'
  if (filter === 'system') {
    if (systemNotifications.length === 0) {
      return (
        <div className="px-2 py-1 text-xs text-popover-foreground dark:text-card-foreground">
          No system notifications
        </div>
      );
    }

    return (
      <div className="space-y-1">
        {systemNotifications.map(notification => (
          <SystemNotificationItem
            key={notification.id}
            notification={notification}
          />
        ))}
      </div>
    );
  }

  if (notifications.length === 0 && (filter !== 'all' || systemNotifications.length === 0)) {
      return (
        <div className="px-2 py-1 text-xs text-popover-foreground dark:text-card-foreground">
          {filter === 'all' ? 'No notifications' : `No ${filter.replace('_', ' ')} notifications`}
        </div>
      );
  }

  // When showing all, display grouped by priority + system notifications
  if (filter === 'all') {
    return (
      <div className="space-y-1">
        {systemNotifications.length > 0 && (
          <div>
            <div className="px-2 py-1 text-xs text-popover-foreground dark:text-card-foreground">System</div>
            <div className="space-y-1">
              {systemNotifications.slice(0, 3).map(notification => (
                <SystemNotificationItem
                  key={notification.id}
                  notification={notification}
                />
              ))}
              {systemNotifications.length > 3 && (
                <p className="text-2xs text-muted-foreground text-center py-0.5">
                  +{systemNotifications.length - 3} more in System tab
                </p>
              )}
            </div>
          </div>
        )}

        {/* DB notifications grouped by priority */}
        {PRIORITY_GROUPS.map(({ priority, label }) => {
          const group = groupedNotifications.get(priority);
          if (!group || group.length === 0) return null;

          return (
            <div key={priority}>
              <div className="px-2 py-1 text-xs text-popover-foreground dark:text-card-foreground">{label}</div>
              <div className="space-y-1">
                {group.map(notification => (
                  <NotificationItem
                    key={notification.id}
                    notification={notification}
                  />
                ))}
              </div>
            </div>
          );
        })}
      </div>
    );
  }

  // When filtered, show flat list
  return (
    <div className="space-y-1">
      {notifications.map(notification => (
        <NotificationItem
          key={notification.id}
          notification={notification}
        />
      ))}
    </div>
  );
}
